import axios, { AxiosResponse } from 'axios'
// import { SessionOptions } from 'iron-session'
import { IronSessionOptions } from 'iron-session'
import { withIronSessionApiRoute } from 'iron-session/next'
import { NextApiHandler } from 'next'
import { useCallback, useContext, useState } from 'react'

import { FacebookEvent, triggerFacebookEvent } from '@lib/facebook'
import { ErrorMessages } from '@lib/helpers'
import { LanguageContext } from '@lib/language'
import { ShopContext } from '@lib/shop-context'
import { ParseStatus } from '@lib/shopify/graphql/client'
import { createUser } from '@lib/shopify/graphql/customer'
import { SiteContext } from '@lib/site'
import { UserContext } from './context'
import {
  ApiAuthResponse,
  LoginFormValues,
  LogoutPayload,
  SignupFormValues,
} from './types'

export const sessionOptions: IronSessionOptions = {
  password: {
    1: process.env.SECRET_COOKIE_PASSWORD ?? '',
  },
  cookieName: 'nhl_auth',
}

/**
 * Gets user session wrapper for API pages.
 */
export const withSessionRoute = (handler: NextApiHandler) =>
  withIronSessionApiRoute(handler, sessionOptions)

/**
 * Returns a method that logs in a user.
 */
export const useLoginUser = () => {
  const { locale } = useContext(LanguageContext)
  const { mutateUser } = useContext(UserContext)

  return useCallback(
    async (payload: LoginFormValues) => {
      try {
        const apiLoginResponse = await axios.post<
          ApiAuthResponse,
          AxiosResponse<ApiAuthResponse>,
          LoginFormValues
        >('/api/auth/login', payload, {
          headers: {
            'Content-Type': 'application/json',
            'X-Locale': locale,
          },
        })
        const { user, ...parseResults } = apiLoginResponse.data
        mutateUser(user)

        return parseResults
      } catch (error) {
        console.error(error)

        return {
          fieldErrors: {},
          errorCount: 0,
          status: ParseStatus.UNKNOWN_ERROR,
        }
      }
    },
    [locale, mutateUser],
  )
}

/**
 * Returns a method that logs out a user.
 */
export const useLogoutUser = () => {
  const { locale } = useContext(LanguageContext)
  const { user, mutateUser } = useContext(UserContext)

  return useCallback(
    async (onSuccess?: () => void) => {
      const payload: LogoutPayload = {
        token: user?.token,
      }
      const apiLogoutResponse = await axios.post<
        ApiAuthResponse,
        AxiosResponse<ApiAuthResponse>,
        LogoutPayload
      >('/api/auth/logout', payload, {
        headers: {
          'Content-Type': 'application/json',
          'X-Locale': locale,
        },
      })

      // Update SWR cache and redirect to the login page
      mutateUser(apiLogoutResponse.data.user)

      if (onSuccess) {
        onSuccess()
      }
    },
    [locale, mutateUser, user?.token],
  )
}

/**
 * User signup form submit handler hook.
 */
export const useSubmitSignupForm = () => {
  const { locale } = useContext(LanguageContext)
  const { settings } = useContext(SiteContext)
  const { shopifyStorefrontClient } = useContext(ShopContext)
  const loginUser = useLoginUser()

  const [errorStatus, setErrorStatus] = useState<ParseStatus>()
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({})

  const handleSignupSubmit = useCallback(
    async (
      values: SignupFormValues,
      onSuccess?: () => Promise<void>,
      onError?: () => Promise<void>,
    ) => {
      if (!shopifyStorefrontClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      setIsLoading(true)
      setIsError(false)

      // Create user
      const createUserResult = await createUser(shopifyStorefrontClient, values)
      setErrorMessages(createUserResult.fieldErrors)

      if (
        createUserResult.status !== ParseStatus.OK ||
        createUserResult.errorCount > 0
      ) {
        setErrorStatus(createUserResult.status)
        setIsError(true)

        if (onError) {
          await onError()
        }
      }

      if (
        createUserResult.status === ParseStatus.OK &&
        createUserResult.errorCount === 0
      ) {
        const { email, password } = values

        if (settings?.facebookEvents) {
          await triggerFacebookEvent(
            FacebookEvent.COMPLETE_REGISTRATION,
            locale,
          )
        }

        // Login after creating user
        const loginUserResult = await loginUser({
          email,
          password,
        })

        setErrorMessages(loginUserResult.fieldErrors)

        if (loginUserResult.status !== ParseStatus.OK) {
          setIsError(true)
        }

        if (
          loginUserResult.status === ParseStatus.OK &&
          loginUserResult.errorCount === 0
        ) {
          if (onSuccess) {
            await onSuccess()
          }
        }

        if (
          loginUserResult.status !== ParseStatus.OK ||
          loginUserResult.errorCount > 0
        ) {
          if (onError) {
            await onError()
          }
        }
      }

      setIsLoading(false)
    },
    [locale, loginUser, settings?.facebookEvents, shopifyStorefrontClient],
  )

  return [
    errorStatus,
    isError,
    isLoading,
    errorMessages,
    handleSignupSubmit,
  ] as const
}
